import React, { useState } from 'react';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { CheckCircle, Clock, AlertTriangle, Camera, Edit, Trash2, QrCode, UserCircle } from 'lucide-react';
import { MONTHS_RO } from './constants';
import OperatorWizard from '../../../components/maintenance/OperatorWizard';

export const MonthlySheet = ({ sheet, month, year, currentMonth, users, currentUserId, onValidateEntry, onSetOperatorBulk, onOpenScanner, onUpdateEntry, onDeleteEntry, detailLoading, hasRole }) => {
  const [editingId, setEditingId] = useState(null);
  const [editFields, setEditFields] = useState({});
  const [showOperatorWizard, setShowOperatorWizard] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const allValidated = sheet.validated === sheet.total;
  const hasOverdue = !allValidated && month < currentMonth;
  const hasPending = sheet.entries.some(e => e.status === 'pending');
  
  // Get all pending entries for bulk operator setting
  const pendingEntries = sheet.entries.filter(e => e.status === 'pending');

  const startEdit = (entry) => {
    setEditingId(entry.id);
    setEditFields({
      operator_utilaj_name: entry.operator_utilaj_name || '',
      data_intretinere: entry.data_intretinere || '',
      nota: entry.nota || '',
    });
  };

  const saveEdit = async (entryId) => {
    await onUpdateEntry(entryId, editFields);
    setEditingId(null);
  };

  const openOperatorWizard = () => {
    setShowOperatorWizard(true);
  };

  const handleOperatorSubmit = async (data) => {
    setSubmitting(true);
    try {
      if (onSetOperatorBulk) {
        await onSetOperatorBulk(data);
      }
      setShowOperatorWizard(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="px-4 pb-4" data-testid={`monthly-sheet-${month}`}>
      {/* Operator Wizard - for bulk setting operator data (NOT validation) */}
      <OperatorWizard
        open={showOperatorWizard}
        onOpenChange={setShowOperatorWizard}
        entries={pendingEntries}
        onSubmit={handleOperatorSubmit}
        submitting={submitting}
      />
      {/* Month header */}
      <div className={`rounded-t-xl px-4 py-3 flex items-center justify-between ${
        allValidated ? 'bg-emerald-600' : hasOverdue ? 'bg-red-600' : 'bg-blue-600'
      }`}>
        <div className="flex items-center gap-3">
          <div className="text-white">
            <h3 className="text-lg font-bold">{MONTHS_RO[month - 1]} {year}</h3>
            <p className="text-xs opacity-80">
              {allValidated ? 'Toate operatiunile validate' : `${sheet.validated}/${sheet.total} validate`}
            </p>
          </div>
        </div>
        {hasPending && month <= currentMonth && hasRole('admin', 'manager', 'technician') && (
          <Button size="sm" variant="secondary" className="h-8 text-xs gap-1 bg-white/20 hover:bg-white/30 text-white border-0"
            onClick={onOpenScanner} disabled={detailLoading} data-testid="validate-qr-btn">
            <Camera className="w-3.5 h-3.5" /> Scaneaza QR
          </Button>
        )}
      </div>

      {/* Add operator button - below header */}
      {hasPending && month <= currentMonth && hasRole('admin', 'manager', 'technician') && (
        <div className="bg-slate-50 border-x border-slate-200 px-4 py-2">
          <Button 
            size="sm" 
            variant="outline" 
            className="h-8 text-xs gap-1.5 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            onClick={openOperatorWizard} 
            disabled={detailLoading || submitting} 
            data-testid="set-operator-btn"
          >
            <UserCircle className="w-4 h-4" /> Adauga operator in formular
          </Button>
          <span className="text-[10px] text-slate-500 ml-2">
            (se aplica pe toate intrarile nevalidate)
          </span>
        </div>
      )}

      {/* Entries per type */}
      <div className="border border-t-0 rounded-b-xl divide-y">
        {sheet.entries.sort((a, b) => a.maintenance_type_name.localeCompare(b.maintenance_type_name)).map(entry => {
          const isVal = entry.status === 'validated';
          const isEditing = editingId === entry.id;
          return (
            <div key={entry.id} className={`p-4 ${isVal ? 'bg-emerald-50/30' : ''}`} data-testid={`entry-${entry.id}`}>
              {/* Type badge + status */}
              <div className="flex items-center justify-between mb-3">
                <Badge className={`text-xs font-semibold px-2.5 ${isVal ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}`}>
                  {entry.maintenance_type_name}
                </Badge>
                <div className="flex items-center gap-2">
                  {isVal ? (
                    <Badge className="bg-emerald-100 text-emerald-700 text-xs gap-1"><CheckCircle className="w-3 h-3" />Validat</Badge>
                  ) : month < currentMonth ? (
                    <Badge className="bg-red-100 text-red-700 text-xs gap-1"><AlertTriangle className="w-3 h-3" />Restant</Badge>
                  ) : (
                    <Badge className="bg-amber-100 text-amber-700 text-xs gap-1"><Clock className="w-3 h-3" />Asteptare</Badge>
                  )}
                </div>
              </div>

              {/* Data grid */}
              <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                <div>
                  <span className="text-[11px] text-slate-400 block mb-0.5">Operator utilaj</span>
                  <div className="h-7 border-b border-slate-300 flex items-end pb-0.5">
                    {entry.operator_utilaj_name ? (
                      <span className="text-sm text-slate-700 font-medium">{entry.operator_utilaj_name}</span>
                    ) : (
                      <span className="text-sm text-slate-400">____________________</span>
                    )}
                  </div>
                </div>
                <div>
                  <span className="text-[11px] text-slate-400 block mb-0.5">Semnatura operator</span>
                  <div className="h-7 border-b border-slate-300 flex items-center justify-center">
                    {entry.operator_utilaj_semnatura ? (
                      <img src={entry.operator_utilaj_semnatura} alt="" className="h-6 object-contain" />
                    ) : (
                      <span className="text-sm text-slate-400">____________________</span>
                    )}
                  </div>
                </div>

                <div>
                  <span className="text-[11px] text-slate-400 block mb-0.5">Data intretinere</span>
                  {isEditing ? (
                    <Input className="h-8 text-xs" value={editFields.data_intretinere} onChange={e => setEditFields({...editFields, data_intretinere: e.target.value})} placeholder="zz.ll.aaaa" />
                  ) : (
                    <span className="text-sm font-mono text-slate-700">{entry.data_intretinere || '—'}</span>
                  )}
                </div>
                <div>
                  <span className="text-[11px] text-slate-400 block mb-0.5">Data verificare</span>
                  <span className="text-sm font-mono text-slate-700">{entry.data_verificare || '—'}</span>
                </div>

                <div>
                  <span className="text-[11px] text-slate-400 block mb-0.5">Responsabil mentenanta</span>
                  <span className="text-sm text-slate-700 font-medium">{entry.responsabil_mentenanta_name || '—'}</span>
                </div>
                <div>
                  <span className="text-[11px] text-slate-400 block mb-0.5">Semnatura responsabil</span>
                  <div className="h-7 flex items-center">
                    {entry.responsabil_semnatura ? (
                      <img src={entry.responsabil_semnatura} alt="" className="h-6 object-contain" />
                    ) : <span className="text-[10px] text-slate-300">—</span>}
                  </div>
                </div>
              </div>

              {entry.operatiuni_realizate && (
                <div className="mt-3 bg-slate-50 rounded-lg px-3 py-2">
                  <span className="text-[11px] text-slate-400 font-semibold">Operatiuni:</span>
                  <p className="text-xs text-slate-600 mt-0.5">{entry.operatiuni_realizate}</p>
                </div>
              )}

              {isEditing && (
                <div className="mt-2">
                  <Label className="text-[11px] text-slate-400">Nota</Label>
                  <Textarea className="text-xs h-16" value={editFields.nota} onChange={e => setEditFields({...editFields, nota: e.target.value})} />
                </div>
              )}
              {!isEditing && entry.nota && (
                <p className="text-xs text-slate-500 mt-2 italic">Nota: {entry.nota}</p>
              )}

              {/* Actions */}
              <div className="flex items-center gap-2 mt-3 pt-2 border-t border-slate-100 flex-wrap">
                {isEditing ? (
                  <>
                    <Button size="sm" className="h-7 text-xs" onClick={() => saveEdit(entry.id)}>Salveaza</Button>
                    <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setEditingId(null)}>Anuleaza</Button>
                  </>
                ) : (
                  <Button size="sm" variant="ghost" className="h-7 text-xs gap-1" onClick={() => startEdit(entry)}>
                    <Edit className="w-3 h-3" /> Editeaza
                  </Button>
                )}
                
                {hasRole('admin', 'manager') && !isEditing && (
                  <Button size="sm" variant="ghost" className="h-7 text-xs text-red-500 ml-auto" onClick={() => onDeleteEntry(entry.id)}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                )}
                {isVal && (
                  <span className="text-[11px] text-emerald-600 ml-auto flex items-center gap-1">
                    {entry.validated_by_qr && <QrCode className="w-3 h-3" />}
                    Validat {entry.data_verificare}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
